import { computed, effect, Injectable, signal } from '@angular/core';
import { ApiService } from '@Services/api.service';
import { StorageService } from '@Services/storage.service';
import { forkJoin, map, merge, mergeMap, of, switchMap, tap } from 'rxjs';
import { environment } from '@environments/environment';
import { IWebUser } from './web-user-authentication.model';
import { AdminUserService } from 'projects/evolutics-sales-ui/src/app/admin/user/user.service';
import { EWebAccessType } from '../../Shared/enums/accessType.enum';
import { IUser } from '@Authentication/auth-extras/authentication.interface';
import { WebUsersService } from '@User/webuser/webuser-extras/webuser.service';

@Injectable({
  providedIn: 'root',
})
export class WebUserAuthenticationService {
  baseURL = environment.gatewayBaseURL + '';
  readonly userKey = 'webUser';
  readonly authUserKey = 'authUser';
  count = 0;
  readonly maxCount = 5;

  get accessType() {
    return environment.accessType;
  }
  readonly _user = signal<IWebUser>(null);
  readonly user = computed(() => this._user());
  readonly _authUser = signal<IUser>(null);
  readonly authUser = computed(() => this._authUser());

  constructor(
    private apiS: ApiService,
    private sS: StorageService,
    private salesPortalUserS: AdminUserService,
    private webUsersService: WebUsersService,
  ) {
    this.getFromLocal();
    // effect(() => {
    //   if (this._user()) this.saveToLocal(this._user());
    // });
  }

  forgetPassword(data: { username: string; loginUrl: string; passwordAuthUrl: string }) {
    return this.apiS.post(`${this.baseURL}/web/access/forgot-password/notification`, {
      ...data,
      access: this.accessType,
    });
  }

  loginUser(username: string, password: string) {
    return this.apiS
      .post<IUser>(`${this.baseURL}/web/access/login`, {
        username,
        password,
        access: this.accessType,
      })
      .pipe(
        map((r) => {
          this.saveAuthToLocal(r);
          return r;
        }),
        mergeMap((r) => this.getUser(r.userDetails.username)),
        map((r) => {
          if (r.access && r.access != this.accessType) throw `User does not have access to this platform`;
          return r;
        }),
        tap((r) => {
          this.saveToLocal(r);
        }),
        switchMap((webUser) =>
          merge(
            webUser.memberCode
              ? this.salesPortalUserS.getMemberViewByMemberCode(webUser.memberCode).pipe(
                  tap((r) => {
                    this.saveToLocal(<IWebUser>{
                      ...webUser,
                      primaryCode: r?.memberData?.primaryCode,
                      teamCat: r?.teamData?.teamCat,
                      _userType: r?.teamData?.teamCat as any,
                    });
                  }),
                )
              : of(webUser),
            webUser.webMenu && this.accessType == EWebAccessType.sales
              ? this.salesPortalUserS
                  .getSalesUserMenuConfig(webUser.webMenu)
                  .pipe(switchMap((r) => this.salesPortalUserS.setSalesMenuItemsIndexFromLocal(r)))
              : of(webUser),
          ),
        ),
      );
  }
  updatePassword(data: { confirmPassword: string; password: string; webAccessType?: EWebAccessType }) {
    if (!data.webAccessType) data.webAccessType = this.accessType;
    return this.apiS.put(`${this.baseURL}/web/access/password`, data);
  }

  logout() {
    this.sS.clear();
    location.href = '/';
  }

  saveAuthToLocal(user: IUser) {
    environment.userSignal.set(user);
    this._authUser.set(user);
    this.sS.saveItem(this.authUserKey, user);
  }

  saveToLocal(user: IWebUser) {
    environment.webUserSignal.set(user);
    this._user.set(user);
    this.sS.saveItem(this.userKey, user);
  }

  getFromLocal() {
    environment.webUserSignal.set(this.sS.getItem(this.userKey));
    environment.userSignal.set(this.sS.getItem(this.authUserKey));
    return environment.webUser;
  }

  protected getUser(username: string) {
    return this.webUsersService.getWebUserByUsername(username, this.accessType);
  }

  /**
   * Calls the endpoint that updates a user password.
   * @param data
   * @returns
   */
  updatePasswordWithReference(data: {
    emailOrUsername: string;
    password: string;
    confirmPassword: string;
    reference: string;
  }) {
    return this.apiS.put(this.baseURL + `/web/access/password/${data.reference}`, {
      confirmPassword: data.confirmPassword,
      password: data.password,
      username: data.emailOrUsername,
      access: this.accessType,
    });
  }

  /**
   * Calls the endpoint tthat validates the password refrence sent via email
   * @param reference
   * @returns
   */
  validateReference(reference: string) {
    return this.apiS.get<{
      id: number;
      email: string;
      username?: string;
      reference: string;
      passwordAuthUrl: string;
      loginUrl?: any;
      createdOn: string;
      expireOn: string;
      verified?: any;
    }>(`${this.baseURL}/web/access/validate/reference/${reference}`);
  }
}
